"use client";

import { useState } from "react";
import { Search, Filter, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";

interface ShowFiltersProps {
  onSearchChange: (search: string) => void;
  onStatusChange: (status: string) => void;
  onStudioChange: (studioId: string) => void;
  onPriceRangeChange: (min: string, max: string) => void;
  onSortChange: (sort: string) => void;
  studios: Array<{ id: number; name: string }>;
  searchValue: string;
  statusValue: string;
  studioValue: string;
  sortValue: string;
  priceRange: { min: string; max: string };
}

export function ShowFilters({
  onSearchChange,
  onStatusChange,
  onStudioChange,
  onPriceRangeChange,
  onSortChange,
  studios,
  searchValue,
  statusValue,
  studioValue,
  sortValue,
  priceRange,
}: ShowFiltersProps) {
  const [localPriceRange, setLocalPriceRange] = useState(priceRange);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const handlePriceRangeApply = () => {
    onPriceRangeChange(localPriceRange.min, localPriceRange.max);
    setIsFilterOpen(false);
  };

  const clearPriceRange = () => {
    const clearedRange = { min: "", max: "" };
    setLocalPriceRange(clearedRange);
    onPriceRangeChange("", "");
    setIsFilterOpen(false);
  };

  const hasActiveFilters =
    statusValue !== "all" ||
    studioValue !== "all" ||
    priceRange.min ||
    priceRange.max;
  const activeFilterCount = [
    statusValue !== "all",
    studioValue !== "all",
    priceRange.min || priceRange.max,
  ].filter(Boolean).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search shows by name or description..."
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Quick Filters */}
        <div className="flex gap-2">
          <Select value={statusValue} onValueChange={onStatusChange}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>

          <Select value={studioValue} onValueChange={onStudioChange}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Studio" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Studios</SelectItem>
              {studios.map((studio) => (
                <SelectItem key={studio.id} value={studio.id.toString()}>
                  {studio.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={sortValue} onValueChange={onSortChange}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="name">Name</SelectItem>
              <SelectItem value="popular">Popular</SelectItem>
              <SelectItem value="price_low">Price: Low to High</SelectItem>
              <SelectItem value="price_high">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>

          {/* Advanced Filters */}
          <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="relative">
                <Filter className="h-4 w-4 mr-2" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="end">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium">Advanced Filters</h4>
                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        onStatusChange("all");
                        onStudioChange("all");
                        clearPriceRange();
                      }}
                    >
                      Clear All
                    </Button>
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <Label className="text-sm font-medium">Price Range</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div>
                        <Input
                          type="number"
                          placeholder="Min"
                          value={localPriceRange.min}
                          onChange={(e) =>
                            setLocalPriceRange((prev) => ({
                              ...prev,
                              min: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div>
                        <Input
                          type="number"
                          placeholder="Max"
                          value={localPriceRange.max}
                          onChange={(e) =>
                            setLocalPriceRange((prev) => ({
                              ...prev,
                              max: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    size="sm"
                    onClick={handlePriceRangeApply}
                    className="flex-1"
                  >
                    Apply
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsFilterOpen(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2">
          {statusValue !== "all" && (
            <div className="flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm">
              Status: {statusValue}
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-blue-200"
                onClick={() => onStatusChange("all")}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}

          {studioValue !== "all" && (
            <div className="flex items-center gap-1 bg-green-100 text-green-800 px-2 py-1 rounded-md text-sm">
              Studio:{" "}
              {studios.find((s) => s.id.toString() === studioValue)?.name}
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-green-200"
                onClick={() => onStudioChange("all")}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}

          {(priceRange.min || priceRange.max) && (
            <div className="flex items-center gap-1 bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm">
              Price: {priceRange.min || "0"} - {priceRange.max || "∞"}
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-purple-200"
                onClick={clearPriceRange}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
